/* ==========================================================================
   GHOUL GOODS — "CRYPT" behaviour engine
   Vanilla ES2018. No dependencies. Every module is defensive: if a hook
   element is missing, that module quietly does nothing and the rest runs.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.CRYPT || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch = window.matchMedia('(hover: none)').matches;

  /* ---------------------------------------------------------------- utils */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opt) { if (el) el.addEventListener(ev, fn, opt); }

  function store(key, val) {
    try {
      if (val === undefined) return localStorage.getItem('crypt:' + key);
      localStorage.setItem('crypt:' + key, val);
    } catch (e) { /* private mode — fine, just don't persist */ }
    return null;
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* Shopify money formatting, from the shop's money_format string. */
  function money(cents) {
    var fmt = CFG.moneyFormat || '£{{amount}}';
    var value = Number(cents || 0) / 100;

    function withDelims(num, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(num)) return '0';
      var parts = num.toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, name) {
      switch (name) {
        case 'amount': return withDelims(value, 2);
        case 'amount_no_decimals': return withDelims(value, 0);
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        case 'amount_with_space_separator': return withDelims(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelims(value, 0, ' ', ',');
        default: return withDelims(value, 2);
      }
    });
  }

  /* ------------------------------------------------------------- 1. TOAST */

  var Toast = (function () {
    var stack;

    function ensure() {
      if (!stack) {
        stack = document.createElement('div');
        stack.className = 'toast-stack';
        stack.setAttribute('role', 'status');
        stack.setAttribute('aria-live', 'polite');
        document.body.appendChild(stack);
      }
      return stack;
    }

    var TICK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';
    var WARN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v6M12 16.5v.5"/></svg>';

    return function show(msg, kind) {
      var el = document.createElement('div');
      el.className = 'toast' + (kind === 'error' ? ' toast--error' : '');
      el.innerHTML = (kind === 'error' ? WARN : TICK) + '<span>' + esc(msg) + '</span>';
      ensure().appendChild(el);
      setTimeout(function () {
        el.setAttribute('data-leaving', 'true');
        setTimeout(function () { el.remove(); }, 420);
      }, 3600);
    };
  })();

  /* --------------------------------------------------------- 2. AMBIENCE */
  /* Procedural audio — no files to load. A low drone, filtered wind, and
     an occasional distant bell. Always user-initiated, always off first. */

  var Ambience = (function () {
    var ctx = null, master = null, nodes = [], playing = false, bellTimer = null;

    function build() {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return false;
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = 0;
      master.connect(ctx.destination);

      // Two detuned low oscillators = an uneasy drone
      [55, 82.4].forEach(function (freq, i) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = i ? 'triangle' : 'sine';
        osc.frequency.value = freq;
        osc.detune.value = i ? 8 : -6;
        gain.gain.value = i ? 0.05 : 0.09;
        osc.connect(gain).connect(master);
        osc.start();
        nodes.push(osc);
      });

      // Wind: white noise through a slowly sweeping band-pass
      var len = ctx.sampleRate * 4;
      var buf = ctx.createBuffer(1, len, ctx.sampleRate);
      var data = buf.getChannelData(0);
      for (var i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * 0.4;

      var noise = ctx.createBufferSource();
      noise.buffer = buf;
      noise.loop = true;

      var band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = 420;
      band.Q.value = 0.7;

      var noiseGain = ctx.createGain();
      noiseGain.gain.value = 0.16;

      var lfo = ctx.createOscillator();
      var lfoGain = ctx.createGain();
      lfo.frequency.value = 0.045;
      lfoGain.gain.value = 260;
      lfo.connect(lfoGain).connect(band.frequency);
      lfo.start();

      noise.connect(band).connect(noiseGain).connect(master);
      noise.start();
      nodes.push(noise, lfo);

      scheduleBell();
      return true;
    }

    function scheduleBell() {
      bellTimer = setTimeout(function () {
        if (playing && ctx) bell();
        scheduleBell();
      }, 14000 + Math.random() * 22000);
    }

    function bell() {
      var t = ctx.currentTime;
      var osc = ctx.createOscillator();
      var gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(196 * (Math.random() > 0.5 ? 1 : 1.5), t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.10, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 4.5);
      osc.connect(gain).connect(master);
      osc.start(t);
      osc.stop(t + 4.6);
    }

    /* Short UI cue — a soft wooden tick on hover/click. */
    function tick(freq) {
      if (!playing || !ctx) return;
      var t = ctx.currentTime;
      var osc = ctx.createOscillator();
      var gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq || 660, t);
      osc.frequency.exponentialRampToValueAtTime((freq || 660) * 0.55, t + 0.09);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.05, t + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      osc.connect(gain).connect(master);
      osc.start(t);
      osc.stop(t + 0.18);
    }

    function fade(to, secs) {
      if (!ctx || !master) return;
      var t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(to, t + (secs || 1.6));
    }

    return {
      toggle: function () {
        if (!ctx && !build()) { Toast('Audio is not available in this browser.', 'error'); return false; }
        if (ctx.state === 'suspended') ctx.resume();
        playing = !playing;
        fade(playing ? 0.55 : 0, playing ? 2.4 : 0.9);
        return playing;
      },
      isPlaying: function () { return playing; },
      tick: tick
    };
  })();

  /* ---------------------------------------------------- 3. EMBER PARTICLES */

  function initEmbers() {
    if (reduceMotion) return;
    var canvas = $('#ember-canvas');
    if (!canvas || !canvas.getContext) return;

    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0, h = 0, parts = [], raf = null, visible = true;

    var PALETTE = [
      [255, 140, 50],   // ember
      [255, 190, 120],  // warm ash
      [180, 210, 255],  // cold spirit
      [140, 255, 200]   // toxic wisp
    ];

    function resize() {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    }

    function seed() {
      var target = Math.round(Math.min(70, Math.max(18, (w * h) / 26000)));
      parts = [];
      for (var i = 0; i < target; i++) parts.push(make(true));
    }

    function make(anywhere) {
      var c = PALETTE[Math.floor(Math.random() * PALETTE.length)];
      return {
        x: Math.random() * w,
        y: anywhere ? Math.random() * h : h + 12,
        r: 0.7 + Math.random() * 2.2,
        vy: -(0.12 + Math.random() * 0.42),
        drift: (Math.random() - 0.5) * 0.28,
        phase: Math.random() * Math.PI * 2,
        wobble: 0.004 + Math.random() * 0.012,
        alpha: 0.14 + Math.random() * 0.42,
        col: c
      };
    }

    function frame() {
      raf = requestAnimationFrame(frame);
      if (!visible) return;
      ctx.clearRect(0, 0, w, h);

      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        p.phase += p.wobble;
        p.y += p.vy;
        p.x += p.drift + Math.sin(p.phase) * 0.32;

        if (p.y < -20 || p.x < -40 || p.x > w + 40) { parts[i] = make(false); continue; }

        var flick = 0.72 + Math.sin(p.phase * 3.1) * 0.28;
        var a = p.alpha * flick;
        var g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4.5);
        g.addColorStop(0, 'rgba(' + p.col[0] + ',' + p.col[1] + ',' + p.col[2] + ',' + a + ')');
        g.addColorStop(1, 'rgba(' + p.col[0] + ',' + p.col[1] + ',' + p.col[2] + ',0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 4.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    resize();
    frame();

    var rt;
    on(window, 'resize', function () { clearTimeout(rt); rt = setTimeout(resize, 220); });
    on(document, 'visibilitychange', function () { visible = !document.hidden; });
  }

  /* ------------------------------------------------------ 4. CURSOR ORB */

  function initCursor() {
    if (reduceMotion || isTouch) return;
    var orb = document.createElement('div');
    orb.className = 'cursor-orb';
    orb.setAttribute('aria-hidden', 'true');
    document.body.appendChild(orb);

    var tx = window.innerWidth / 2, ty = window.innerHeight / 2, x = tx, y = ty, raf;

    on(document, 'pointermove', function (e) {
      tx = e.clientX; ty = e.clientY;
      var hot = !!(e.target.closest && e.target.closest('a, button, .tile, .card, input, select'));
      orb.setAttribute('data-hot', hot ? 'true' : 'false');
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });

    function loop() {
      x += (tx - x) * 0.45;
      y += (ty - y) * 0.45;
      orb.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
      raf = (Math.abs(tx - x) > 0.4 || Math.abs(ty - y) > 0.4) ? requestAnimationFrame(loop) : null;
    }
  }

  /* --------------------------------------------------------- 5. FX DOCK */

  function initFxDock() {
    var soundBtn = $('[data-fx-sound]');
    var visualBtn = $('[data-fx-visual]');

    if (visualBtn) {
      var saved = store('fx');
      // Default ON for visuals unless the visitor turned it off, or prefers less motion.
      var fxOn = reduceMotion ? false : saved !== 'off';
      apply(fxOn);
      on(visualBtn, 'click', function () { fxOn = !fxOn; apply(fxOn); Ambience.tick(520); });
    }

    function apply(v) {
      document.body.setAttribute('data-fx', v ? 'on' : 'off');
      visualBtn.setAttribute('aria-pressed', v ? 'true' : 'false');
      visualBtn.setAttribute('aria-label', v ? 'Turn off cursor effects' : 'Turn on cursor effects');
      var canvas = $('#ember-canvas');
      if (canvas) canvas.style.display = v ? '' : 'none';
      store('fx', v ? 'on' : 'off');
    }

    if (soundBtn) {
      on(soundBtn, 'click', function () {
        var nowOn = Ambience.toggle();
        soundBtn.setAttribute('aria-pressed', nowOn ? 'true' : 'false');
        soundBtn.setAttribute('aria-label', nowOn ? 'Mute atmosphere' : 'Play atmosphere');
        Toast(nowOn ? 'Atmosphere on.' : 'Atmosphere off.');
      });
    }

    // Subtle UI cues, only audible once the visitor has opted into sound.
    on(document, 'pointerenter', function (e) {
      if (e.target.closest && e.target.closest('.btn, .tile, .quiz__option')) Ambience.tick(740);
    }, true);
  }

  /* ------------------------------------------------------- 6. COUNTDOWN */

  function initCountdown() {
    var root = $('[data-countdown]');
    if (!root) return;

    var nums = {
      d: $('[data-cd="d"]', root), h: $('[data-cd="h"]', root),
      m: $('[data-cd="m"]', root), s: $('[data-cd="s"]', root)
    };

    function target() {
      var now = new Date();
      var end = new Date(now.getFullYear(), 9, 31, 23, 59, 59); // 31 Oct
      if (now > end) end = new Date(now.getFullYear() + 1, 9, 31, 23, 59, 59);
      return end;
    }

    var end = target();
    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function tick() {
      var diff = Math.max(0, end - new Date());
      var s = Math.floor(diff / 1000);
      var vals = {
        d: String(Math.floor(s / 86400)),
        h: pad(Math.floor((s % 86400) / 3600)),
        m: pad(Math.floor((s % 3600) / 60)),
        s: pad(s % 60)
      };
      Object.keys(nums).forEach(function (k) {
        if (nums[k] && nums[k].textContent !== vals[k]) nums[k].textContent = vals[k];
      });
    }

    tick();
    setInterval(tick, 1000);
  }

  /* --------------------------------------------------------- 7. REVEALS */

  function initReveals() {
    var items = $$('[data-reveal]');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-revealed');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    items.forEach(function (el) { io.observe(el); });

    // Auto-stagger direct children of any .stagger container
    $$('.stagger').forEach(function (group) {
      Array.prototype.forEach.call(group.children, function (child, i) {
        child.style.setProperty('--reveal-delay', (i * 0.07).toFixed(2) + 's');
      });
    });
  }

  /* --------------------------------------------------- 7b. NAV DROPDOWNS */
  /* Hover and focus-within already open the "Catalog" dropdown for anyone
     with a pointer or a keyboard (see crypt.css). This layer only adds what
     CSS can't: a tap on a coarse-pointer device opens the menu instead of
     immediately following the link, and everything closes on Escape or a
     tap elsewhere. */

  function initNavDropdowns() {
    var items = $$('.nav__item--has-dropdown');
    if (!items.length) return;

    function closeAll(except) {
      items.forEach(function (item) {
        if (item === except) return;
        item.setAttribute('data-open', 'false');
        var trig = $('[data-nav-trigger]', item);
        if (trig) trig.setAttribute('aria-expanded', 'false');
      });
    }

    items.forEach(function (item) {
      var trigger = $('[data-nav-trigger]', item);
      if (!trigger) return;

      on(trigger, 'click', function (e) {
        if (!isTouch) return;
        var isOpen = item.getAttribute('data-open') === 'true';
        if (isOpen) return; // second tap follows the link as normal
        e.preventDefault();
        closeAll(item);
        item.setAttribute('data-open', 'true');
        trigger.setAttribute('aria-expanded', 'true');
      });
    });

    on(document, 'click', function (e) {
      if (!e.target.closest('.nav__item--has-dropdown')) closeAll();
    });

    on(document, 'focusin', function (e) {
      if (!e.target.closest('.nav__item--has-dropdown')) closeAll();
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  /* ------------------------------------------------ 7c. MOBILE NAV GROUPS */

  function initMobileNavGroups() {
    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-mobile-nav-toggle]');
      if (!btn) return;
      var group = btn.closest('.mobile-nav__group');
      var panel = group && $('[data-mobile-nav-submenu]', group);
      if (!panel) return;
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
      if (!open) Ambience.tick(600);
    });
  }

  /* ------------------------------------------------- 8. HEADER & PARALLAX */

  function initHeader() {
    var header = $('.site-header');
    if (!header) return;
    var ticking = false;

    function update() {
      header.setAttribute('data-stuck', window.scrollY > 24 ? 'true' : 'false');
      ticking = false;
    }

    update();
    on(window, 'scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
  }

  function initParallax() {
    if (reduceMotion) return;
    var layers = $$('[data-parallax]');
    if (!layers.length) return;
    var ticking = false;

    function update() {
      var y = window.scrollY;
      layers.forEach(function (el) {
        var rate = parseFloat(el.getAttribute('data-parallax')) || 0.2;
        el.style.transform = 'translate3d(0,' + (y * rate).toFixed(1) + 'px,0)';
      });
      ticking = false;
    }

    on(window, 'scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* Split the hero headline into per-word spans so each can rise in turn. */
  function initHeroWords() {
    var title = $('[data-split-words]');
    if (!title) return;
    var words = title.textContent.trim().split(/\s+/);
    title.textContent = '';
    words.forEach(function (w, i) {
      var outer = document.createElement('span');
      outer.className = 'word';
      var inner = document.createElement('span');
      inner.style.setProperty('--i', i);
      inner.textContent = w;
      outer.appendChild(inner);
      title.appendChild(outer);
      if (i < words.length - 1) title.appendChild(document.createTextNode(' '));
    });
  }

  /* Pointer-tracked ember bloom on collection tiles. */
  function initTiles() {
    if (isTouch) return;
    $$('.tile').forEach(function (tile) {
      on(tile, 'pointermove', function (e) {
        var r = tile.getBoundingClientRect();
        tile.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
        tile.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
      }, { passive: true });
    });
  }

  /* Duplicate marquee content so the loop is seamless. */
  function initMarquee() {
    $$('.marquee').forEach(function (m) {
      var track = $('.marquee__track', m);
      if (!track) return;
      // A second identical track sits flush against the first, so when both
      // slide left by 100% the seam is invisible and the loop stays continuous.
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      m.appendChild(clone);
    });
  }

  /* ---------------------------------------------------------- 9. DRAWERS */

  var Drawers = (function () {
    var openEl = null, lastFocus = null;

    function open(id) {
      var el = document.getElementById(id);
      if (!el) return;
      if (openEl && openEl !== el) close();
      lastFocus = document.activeElement;
      el.setAttribute('data-open', 'true');
      openEl = el;
      document.body.style.overflow = 'hidden';
      var focusable = $('input, button, a[href]', el);
      setTimeout(function () { if (focusable) focusable.focus(); }, 340);
      Ambience.tick(480);
    }

    function close() {
      if (!openEl) return;
      openEl.setAttribute('data-open', 'false');
      openEl = null;
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    on(document, 'click', function (e) {
      var opener = e.target.closest('[data-drawer-open]');
      if (opener) { e.preventDefault(); open(opener.getAttribute('data-drawer-open')); return; }
      if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer__scrim')) close();
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape') close();
      if (e.key !== 'Tab' || !openEl) return;
      var f = $$('a[href], button:not([disabled]), input, select, textarea', openEl)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    return { open: open, close: close };
  })();

  /* ------------------------------------------------------------- 10. CART */

  var Cart = (function () {
    var routes = CFG.routes || {};
    var threshold = Number(CFG.freeShippingThreshold || 0) * 100;

    function render(cart) {
      updateCount(cart.item_count);
      var body = $('[data-cart-body]');
      var foot = $('[data-cart-foot]');
      if (!body) return;

      if (!cart.item_count) {
        body.innerHTML =
          '<div class="cart-empty">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.1"><path d="M4 7h16l-1.4 11.2A2 2 0 0 1 16.6 20H7.4a2 2 0 0 1-2-1.8L4 7Z"/><path d="M9 7V5.5a3 3 0 0 1 6 0V7"/></svg>' +
          '<p class="muted">Your crypt is empty.</p>' +
          '<a class="btn" href="' + esc(routes.all || '/collections/all') + '">Start haunting</a>' +
          '</div>';
        if (foot) foot.hidden = true;
        return;
      }

      if (foot) foot.hidden = false;

      body.innerHTML = cart.items.map(function (item, i) {
        var img = item.image
          ? '<img class="cart-line__img" src="' + esc(item.image.replace(/(\.[a-z]+)(\?|$)/i, '_180x$1$2')) + '" alt="" loading="lazy" width="82" height="90">'
          : '<div class="cart-line__img"></div>';
        var variant = (item.variant_title && item.variant_title !== 'Default Title')
          ? '<div class="cart-line__variant">' + esc(item.variant_title) + '</div>' : '';
        var unit = item.original_line_price > item.final_line_price
          ? '<s>' + money(item.original_line_price) + '</s> <span class="on-sale">' + money(item.final_line_price) + '</span>'
          : money(item.final_line_price);

        return '<div class="cart-line" data-line="' + (i + 1) + '">' +
          '<a href="' + esc(item.url) + '">' + img + '</a>' +
          '<div class="cart-line__body">' +
            '<a class="cart-line__title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
            variant +
            '<div class="qty">' +
              '<button type="button" data-cart-minus aria-label="Decrease quantity">&minus;</button>' +
              '<input type="number" min="0" value="' + item.quantity + '" data-cart-qty aria-label="Quantity">' +
              '<button type="button" data-cart-plus aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="cart-line__remove" data-cart-remove>Remove</button>' +
          '</div>' +
          '<div class="cart-line__price">' + unit + '</div>' +
        '</div>';
      }).join('');

      var totalEl = $('[data-cart-total]');
      if (totalEl) totalEl.textContent = money(cart.total_price);

      renderMeter(cart.total_price);
    }

    function renderMeter(total) {
      var meter = $('[data-ship-meter]');
      if (!meter) return;
      if (!threshold) { meter.hidden = true; return; }
      meter.hidden = false;
      var pct = Math.min(100, (total / threshold) * 100);
      var fill = $('.ship-meter__fill', meter);
      var label = $('.ship-meter__label', meter);
      if (fill) fill.style.width = pct + '%';
      if (label) {
        label.innerHTML = total >= threshold
          ? 'Free UK delivery unlocked. &#127881;'
          : 'Spend <b>' + money(threshold - total) + '</b> more for free UK delivery.';
      }
    }

    function updateCount(n) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = n;
        el.setAttribute('data-has-items', n > 0 ? 'true' : 'false');
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        if (n > 0) el.classList.add('is-bumped');
      });
    }

    function fetchCart(openDrawer) {
      return fetch(routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          render(cart);
          if (openDrawer) Drawers.open('cart-drawer');
          return cart;
        })
        .catch(function () { /* leave the DOM as-is */ });
    }

    /* Animate the product image into the cart icon. Pure garnish, but it
       makes the add feel physical. */
    function fly(sourceImg) {
      if (reduceMotion || !sourceImg) return;
      var target = $('[data-cart-icon]');
      if (!target) return;
      var from = sourceImg.getBoundingClientRect();
      var to = target.getBoundingClientRect();

      var ghost = document.createElement('img');
      ghost.src = sourceImg.currentSrc || sourceImg.src;
      ghost.className = 'fly-ghost';
      ghost.style.cssText = 'left:' + from.left + 'px;top:' + from.top + 'px;width:' + from.width + 'px;height:' + from.height + 'px;opacity:1;';
      document.body.appendChild(ghost);

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          ghost.style.transform = 'translate(' + (to.left + to.width / 2 - from.left - from.width / 2) + 'px,' +
            (to.top + to.height / 2 - from.top - from.height / 2) + 'px) rotate(12deg)';
          ghost.style.width = '26px';
          ghost.style.height = '26px';
          ghost.style.opacity = '0.15';
        });
      });

      setTimeout(function () { ghost.remove(); }, 900);
    }

    function add(id, qty, sourceImg) {
      var body = { items: [{ id: Number(id), quantity: Number(qty) || 1 }] };
      return fetch(routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.data.description || res.data.message || 'Could not add to cart.');
          fly(sourceImg);
          Ambience.tick(880);
          return fetchCart(true);
        })
        .catch(function (err) { Toast(err.message || 'Could not add to cart.', 'error'); });
    }

    function change(line, quantity) {
      return fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          render(cart);
          if (document.body.getAttribute('data-template') === 'cart') window.location.reload();
          return cart;
        })
        .catch(function () { Toast('Could not update the cart.', 'error'); });
    }

    /* --- delegated handlers --- */

    on(document, 'click', function (e) {
      var addBtn = e.target.closest('[data-add-to-cart]');
      if (addBtn) {
        e.preventDefault();
        var form = addBtn.closest('form');
        var id = addBtn.getAttribute('data-variant-id') ||
          (form && form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value);
        if (!id) { Toast('Please choose an option first.', 'error'); return; }
        var qtyInput = form && form.querySelector('[name="quantity"]');
        var scope = addBtn.closest('.card, .product-layout, form') || document;
        var img = scope.querySelector('img');

        var label = addBtn.querySelector('[data-btn-label]') || addBtn;
        var original = label.textContent;
        label.textContent = 'Summoning…';
        addBtn.setAttribute('aria-disabled', 'true');

        add(id, qtyInput ? qtyInput.value : 1, img).then(function () {
          label.textContent = original;
          addBtn.removeAttribute('aria-disabled');
        });
        return;
      }

      var line = e.target.closest('[data-line]');
      if (!line) return;
      var n = line.getAttribute('data-line');
      var input = $('[data-cart-qty]', line);

      if (e.target.closest('[data-cart-remove]')) { change(n, 0); }
      else if (e.target.closest('[data-cart-plus]')) { change(n, Number(input.value) + 1); }
      else if (e.target.closest('[data-cart-minus]')) { change(n, Math.max(0, Number(input.value) - 1)); }
    });

    on(document, 'change', function (e) {
      var input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      var line = input.closest('[data-line]');
      if (line) change(line.getAttribute('data-line'), input.value);
    });

    return { fetch: fetchCart, render: render, add: add };
  })();

  /* -------------------------------------------------------- 11. QUANTITY */

  function initQty() {
    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-qty-step]');
      if (!btn) return;
      var wrap = btn.closest('.qty');
      var input = $('input', wrap);
      if (!input) return;
      var step = Number(btn.getAttribute('data-qty-step'));
      var min = Number(input.getAttribute('min') || 1);
      input.value = Math.max(min, Number(input.value || 1) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------- 12. ACCORDION */

  function initAccordion() {
    on(document, 'click', function (e) {
      var trig = e.target.closest('.accordion__trigger');
      if (!trig) return;
      var open = trig.getAttribute('aria-expanded') === 'true';
      trig.setAttribute('aria-expanded', open ? 'false' : 'true');
      var panel = document.getElementById(trig.getAttribute('aria-controls'));
      if (panel) panel.setAttribute('data-open', open ? 'false' : 'true');
      Ambience.tick(600);
    });
  }

  /* --------------------------------------------------------- 13. GALLERY */

  function initGallery() {
    var gallery = $('[data-gallery]');
    if (!gallery) return;
    var main = $('[data-gallery-main]', gallery);

    on(gallery, 'click', function (e) {
      var thumb = e.target.closest('[data-gallery-thumb]');
      if (!thumb || !main) return;
      $$('[data-gallery-thumb]', gallery).forEach(function (t) { t.setAttribute('aria-current', 'false'); });
      thumb.setAttribute('aria-current', 'true');
      main.style.opacity = '0';
      setTimeout(function () {
        main.src = thumb.getAttribute('data-full');
        main.alt = thumb.getAttribute('data-alt') || '';
        main.style.opacity = '1';
      }, 180);
    });
  }

  /* --------------------------------------------------- 14. VARIANT PICKER */

  function initVariants() {
    var root = $('[data-variants]');
    if (!root) return;

    var data;
    try { data = JSON.parse($('[data-variant-json]', root).textContent); } catch (e) { return; }

    var idInput = $('[name="id"]', root.closest('form') || document);
    var priceEl = $('[data-variant-price]');
    var stockEl = $('[data-variant-stock]');
    var btn = $('[data-add-to-cart]', root.closest('form') || document);

    function selected() {
      return $$('[data-option-index]', root).map(function (group) {
        var active = $('[aria-pressed="true"]', group);
        return active ? active.getAttribute('data-value') : null;
      });
    }

    function match() {
      var sel = selected();
      return data.find(function (v) {
        return v.options.every(function (o, i) { return sel[i] == null || o === sel[i]; });
      });
    }

    function update() {
      var v = match();
      if (!v) return;
      if (idInput) idInput.value = v.id;
      if (btn) btn.setAttribute('data-variant-id', v.id);

      if (priceEl) {
        priceEl.innerHTML = v.compare_at_price && v.compare_at_price > v.price
          ? money(v.price) + ' <s>' + money(v.compare_at_price) + '</s>'
          : money(v.price);
      }

      if (stockEl) {
        var cls = v.available ? (v.inventory_quantity > 0 && v.inventory_quantity < 6 ? 'stock-dot--low' : '') : 'stock-dot--out';
        var text = v.available
          ? (v.inventory_quantity > 0 && v.inventory_quantity < 6 ? 'Only ' + v.inventory_quantity + ' left in the crypt' : 'In stock, ships within 48 hours')
          : 'Currently out of stock';
        stockEl.innerHTML = '<span class="stock-dot ' + cls + '"></span><span>' + text + '</span>';
      }

      if (btn) {
        var label = $('[data-btn-label]', btn) || btn;
        label.textContent = v.available ? 'Add to cart' : 'Sold out';
        btn.setAttribute('aria-disabled', v.available ? 'false' : 'true');
      }

      // Reflect the choice in the URL so the variant is shareable.
      if (window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    on(root, 'click', function (e) {
      var opt = e.target.closest('[data-value]');
      if (!opt) return;
      var group = opt.closest('[data-option-index]');
      $$('[data-value]', group).forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
      opt.setAttribute('aria-pressed', 'true');
      Ambience.tick(700);
      update();
    });

    update();
  }

  /* ------------------------------------------------ 15. PREDICTIVE SEARCH */

  function initSearch() {
    var input = $('[data-search-input]');
    var out = $('[data-search-results]');
    if (!input || !out) return;
    var timer, controller;

    on(input, 'input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }

      timer = setTimeout(function () {
        if (controller) controller.abort();
        controller = new AbortController();
        var url = (CFG.routes.predictive || '/search/suggest') +
          '.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';

        fetch(url, { signal: controller.signal, headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (d) {
            var items = (d.resources && d.resources.results && d.resources.results.products) || [];
            if (!items.length) { out.innerHTML = '<p class="muted">Nothing stirs. Try another word.</p>'; return; }
            out.innerHTML = items.map(function (p) {
              var img = p.featured_image && p.featured_image.url
                ? '<img src="' + esc(p.featured_image.url) + '" alt="" loading="lazy" width="62" height="62">'
                : '<div style="width:62px;height:62px;background:var(--slab)"></div>';
              return '<a class="search-result" href="' + esc(p.url) + '">' + img +
                '<span class="search-result__title">' + esc(p.title) + '</span>' +
                '<span class="search-result__price">' + esc(p.price ? money(Math.round(parseFloat(p.price) * 100)) : '') + '</span></a>';
            }).join('');
          })
          .catch(function () { /* aborted or offline */ });
      }, 220);
    });
  }

  /* ------------------------------------------------------------ 16. QUIZ */

  function initQuiz() {
    var root = $('[data-quiz]');
    if (!root) return;

    var catalogue = [];
    try { catalogue = JSON.parse($('[data-quiz-products]', root).textContent); } catch (e) { catalogue = []; }

    // The results panel is also a .quiz__step, but it must not be counted as a
    // question — otherwise the last answer advances to an empty panel instead
    // of running conjure().
    var steps = $$('.quiz__step', root).filter(function (s) {
      return !s.hasAttribute('data-quiz-stage');
    });
    var pips = $$('.quiz__pip', root);
    var answers = {};
    var index = 0;

    /* ---- matching model -------------------------------------------------
       Earlier versions scored every answer as a loose substring match against
       one blob of text, which let a pet harness win a question about dressing
       an adult. Audience is now a hard filter (getting it wrong makes the
       recommendation useless), while vibe and budget are weighted preferences.
       ------------------------------------------------------------------- */

    var AUDIENCE = {
      adult: {
        must: ['adult', 'men', 'women', 'unisex'],
        types: ['costumes', 'masks', 'jewellery', 'jewelry'],
        block: ['pet', 'dog', 'cat', 'kids', 'childrens', 'children', 'boys', 'girls']
      },
      kids: {
        must: ['kids', 'childrens', 'children', 'boys', 'girls', 'child'],
        types: ['costumes', 'masks'],
        block: ['pet', 'dog harness']
      },
      pet: {
        must: ['pet', 'dog', 'cat'],
        types: ['pet costumes'],
        block: []
      },
      home: {
        must: ['decor', 'decoration', 'lighting', 'party', 'prop', 'ornament', 'garland', 'centrepiece'],
        types: ['decorations', 'lighting', 'party supplies'],
        block: []
      }
    };

    var VIBE = {
      horror:  ['horror', 'scary', 'creepy', 'skull', 'skeleton', 'ghost', 'demon', 'zombie', 'killer', 'blood', 'gore', 'clown', 'reaper', 'werewolf', 'possessed'],
      funny:   ['funny', 'novelty', 'inflatable', 'prank', 'banana', 'fancy dress', 'silly', 'meme'],
      gothic:  ['gothic', 'elegant', 'masquerade', 'lace', 'venetian', 'velvet', 'vampire', 'witch', 'jewellery', 'jewelry', 'choker', 'victorian'],
      cosy:    ['cute', 'glow', 'autumn', 'maple', 'harvest', 'pumpkin', 'luminous', 'candle', 'warm', 'string lights']
    };

    /* Everything a product can be matched on, lower-cased once up front. */
    function haystack(p) {
      if (p._hay) return p._hay;
      p._hay = {
        tags: (p.tags || []).map(function (t) { return String(t).toLowerCase(); }),
        type: String(p.type || '').toLowerCase(),
        title: String(p.title || '').toLowerCase()
      };
      return p._hay;
    }

    function hasAny(p, words) {
      var h = haystack(p);
      for (var i = 0; i < words.length; i++) {
        var w = words[i];
        if (h.tags.indexOf(w) > -1) return true;
        if (h.type.indexOf(w) > -1) return true;
        if (h.title.indexOf(w) > -1) return true;
      }
      return false;
    }

    function countMatches(p, words) {
      var h = haystack(p), n = 0;
      words.forEach(function (w) {
        if (h.tags.indexOf(w) > -1) n += 2;        // an explicit tag is a strong signal
        else if (h.type.indexOf(w) > -1) n += 2;
        else if (h.title.indexOf(w) > -1) n += 1;  // a title mention is weaker
      });
      return n;
    }

    function audienceOk(p, key) {
      var rule = AUDIENCE[key];
      if (!rule) return true;
      if (rule.block.length && hasAny(p, rule.block)) return false;
      if (hasAny(p, rule.must)) return true;
      var h = haystack(p);
      return rule.types.some(function (t) { return h.type.indexOf(t) > -1; });
    }

    function score(p) {
      var s = 0;

      if (answers.vibe) {
        var hits = countMatches(p, VIBE[answers.vibe] || []);
        s += hits * 3;
        if (hits === 0) s -= 2;
      }

      if (answers.budget) {
        var price = Number(p.price) / 100;
        var b = answers.budget;
        if (price >= b.min && price <= b.max) s += 6;
        else if (price < b.min) s += 2;                       // under budget is fine
        else if (price <= b.max * 1.25) s += 1;               // slightly over, still shown
        else s -= 5;
      }

      if (p.available) s += 2;
      return s;
    }

    /* Returns 4 products, relaxing constraints in order rather than ever
       showing an empty result. Each pass records how it was reached so the
       copy can be honest about a widened match. */
    function rank() {
      var passes = [
        { label: 'exact',    filter: function (p) { return audienceOk(p, answers.who); } },
        { label: 'audience', filter: function (p) { return audienceOk(p, answers.who); }, dropBudget: true },
        { label: 'any',      filter: function () { return true; }, dropBudget: true }
      ];

      for (var i = 0; i < passes.length; i++) {
        var pass = passes[i];
        var savedBudget = answers.budget;
        if (pass.dropBudget) answers.budget = null;

        var list = catalogue.filter(pass.filter).map(function (p) {
          return { p: p, s: score(p) };
        }).filter(function (x) { return x.s > 0; })
          .sort(function (a, b) { return b.s - a.s || a.p.price - b.p.price; });

        answers.budget = savedBudget;

        if (list.length >= 4 || (list.length && i === passes.length - 1)) {
          return { items: list.slice(0, 4).map(function (x) { return x.p; }), pass: pass.label };
        }
        if (list.length && i === 0 && list.length >= 2) {
          return { items: list.slice(0, 4).map(function (x) { return x.p; }), pass: pass.label };
        }
      }
      return { items: catalogue.slice(0, 4), pass: 'fallback' };
    }

    function go(i, scroll) {
      index = i;
      var stage = $('[data-quiz-stage]', root);
      if (stage) stage.setAttribute('data-active', 'false');
      steps.forEach(function (s, n) { s.setAttribute('data-active', n === i ? 'true' : 'false'); });
      pips.forEach(function (p, n) { p.setAttribute('data-done', n <= i ? 'true' : 'false'); });
      if (scroll) root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }

    var HEADLINE = {
      exact:    'Your Halloween, assembled',
      audience: 'Close enough to be worth a look',
      any:      'Nothing matched exactly — try these',
      fallback: 'A few to start you off'
    };

    function conjure() {
      var stage = $('[data-quiz-stage]', root);
      steps.forEach(function (s) { s.setAttribute('data-active', 'false'); });
      stage.innerHTML = '<div class="quiz__conjuring"><div class="cauldron"></div><span>Consulting the spirits…</span></div>';
      stage.setAttribute('data-active', 'true');

      setTimeout(function () {
        var res = rank();

        if (!res.items.length) {
          stage.innerHTML = '<div class="quiz__result"><h3>Nothing matched.</h3>' +
            '<a class="btn" href="' + esc(CFG.routes.all || '/collections/all') + '">Browse everything</a></div>';
          return;
        }

        stage.innerHTML =
          '<div class="quiz__result">' +
            '<span class="eyebrow">The spirits have spoken</span>' +
            '<h3>' + esc(HEADLINE[res.pass] || HEADLINE.exact) + '</h3>' +
            '<div class="product-grid">' + res.items.map(cardHtml).join('') + '</div>' +
            '<div class="quiz__actions">' +
              '<button type="button" class="btn btn--ghost" data-quiz-restart>Ask again</button>' +
              '<a class="btn" href="' + esc(CFG.routes.all || '/collections/all') + '">See everything</a>' +
            '</div>' +
          '</div>';
        Ambience.tick(900);
      }, 1100);
    }

    function cardHtml(p) {
      var img = p.image
        ? '<img class="card__img--main" src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy">'
        : '<div class="card__placeholder"></div>';
      var buy = p.available
        ? '<div class="card__quick"><button type="button" class="btn btn--block" data-add-to-cart data-variant-id="' + esc(p.variant) + '"><span data-btn-label>Add to cart</span></button></div>'
        : '<div class="card__badges"><span class="badge badge--out">Sold out</span></div>';
      return '<article class="card">' +
        '<div class="card__media">' + img + buy + '</div>' +
        '<div class="card__body">' +
          '<h4 class="card__title">' + esc(p.title) + '</h4>' +
          '<div class="card__price">' + money(p.price) + '</div>' +
        '</div>' +
        '<a class="card__link" href="' + esc(p.url) + '" aria-label="' + esc(p.title) + '"></a>' +
      '</article>';
    }

    on(root, 'click', function (e) {
      var opt = e.target.closest('.quiz__option');
      if (opt) {
        var key = opt.getAttribute('data-key');
        var raw = opt.getAttribute('data-value');
        answers[key] = key === 'budget'
          ? { min: Number(raw.split('-')[0]), max: Number(raw.split('-')[1]) }
          : raw;
        Ambience.tick(820);
        if (index < steps.length - 1) go(index + 1, true); else conjure();
        return;
      }

      if (e.target.closest('[data-quiz-restart]')) {
        answers = {};
        var stage = $('[data-quiz-stage]', root);
        stage.setAttribute('data-active', 'false');
        stage.innerHTML = '';
        go(0, true);
      }
    });

    go(0, false);
  }


  /* ---------------------------------------------------- 16b. POPUPS */
  /* Welcome (email -> code) and exit-intent. Both are opt-in from theme
     settings, both remember dismissal, and neither ever shows over the top
     of the other. */

  var Popups = (function () {

    /* Practical RFC-ish validation. Deliberately stricter than type="email",
       which accepts "a@b" — that passes the browser and then bounces. */
    function validEmail(v) {
      v = String(v || '').trim();
      if (v.length < 6 || v.length > 254) return false;
      if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,63}$/.test(v)) return false;
      var parts = v.split('@');
      var local = parts[0], domain = parts[1];
      if (local.charAt(0) === '.' || local.charAt(local.length - 1) === '.') return false;
      if (local.indexOf('..') > -1 || domain.indexOf('..') > -1) return false;
      if (domain.charAt(0) === '-' || domain.indexOf('.-') > -1) return false;
      return true;
    }

    var openEl = null, lastFocus = null, lastClosedAt = 0;
    var COOLDOWN = 15000; // don't let one modal follow another straight away

    function busy() {
      return !!openEl || (Date.now() - lastClosedAt) < COOLDOWN;
    }

    function open(el) {
      if (!el || openEl || document.querySelector('.drawer[data-open="true"]')) return;
      lastFocus = document.activeElement;
      el.hidden = false;
      void el.offsetWidth;
      el.setAttribute('data-open', 'true');
      openEl = el;
      document.body.style.overflow = 'hidden';
      var f = el.querySelector('input, button:not([data-popup-close])');
      setTimeout(function () { if (f) f.focus(); }, 320);
      Ambience.tick(520);
    }

    function close() {
      if (!openEl) return;
      var el = openEl;
      el.setAttribute('data-open', 'false');
      openEl = null;
      lastClosedAt = Date.now();
      document.body.style.overflow = '';
      setTimeout(function () { el.hidden = true; }, 420);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    /* Session memory, so both popups return on a visitor's next visit but
       never nag twice in the same browsing session. sessionStorage is
       per-tab and cleared when the tab closes, which is the behaviour a
       shopper reads as "a session". */
    function sessionSeen(key) {
      try { return sessionStorage.getItem('crypt:' + key) === '1'; }
      catch (e) { return false; }
    }

    function markSession(key) {
      try { sessionStorage.setItem('crypt:' + key, '1'); } catch (e) {}
    }

    /* --- welcome --- */
    function initWelcome() {
      var el = $('[data-popup="welcome"]');
      if (!el) return;

      // Signing up is the only thing that stops this permanently.
      if (store('subscribed') === 'yes') return;
      if (sessionSeen('popup:welcome')) return;

      var delay = (Number(el.getAttribute('data-delay')) || 6) * 1000;
      setTimeout(function () {
        if (busy()) return;
        markSession('popup:welcome');
        open(el);
      }, delay);

      var form = $('[data-popup-form]', el);
      var input = $('input[type="email"]', form);
      var errEl = $('[data-popup-error]', el);
      var label = $('[data-popup-submit-label]', el);

      function showError(msg) {
        errEl.textContent = msg;
        errEl.hidden = false;
        input.setAttribute('aria-invalid', 'true');
      }

      function clearError() {
        errEl.hidden = true;
        input.removeAttribute('aria-invalid');
      }

      on(input, 'input', clearError);

      on(form, 'submit', function (e) {
        e.preventDefault();
        var value = input.value.trim();

        if (!value) { showError('Enter your email address first.'); input.focus(); return; }
        if (!validEmail(value)) { showError('That does not look like a valid email address.'); input.focus(); return; }

        clearError();
        var original = label.textContent;
        label.textContent = 'Summoning…';

        // Shopify's own customer form endpoint — adds them to the marketing
        // list exactly as the native form would, without a page reload.
        var fd = new FormData();
        fd.append('form_type', 'customer');
        fd.append('utf8', '✓');
        fd.append('contact[email]', value);
        fd.append('contact[tags]', 'newsletter,welcome-popup');

        fetch('/contact', { method: 'POST', body: fd, headers: { 'Accept': 'text/html' } })
          .catch(function () { /* still reveal the code — the email is captured client-side */ })
          .then(function () {
            store('subscribed', 'yes');
            $('[data-popup-step="form"]', el).hidden = true;
            $('[data-popup-step="reveal"]', el).hidden = false;
            label.textContent = original;
            Ambience.tick(900);
          });
      });

      on(el, 'click', function (e) {
        if (e.target.closest('[data-popup-close]')) close();
      });
    }

    /* --- exit intent --- */
    function initExit() {
      var el = $('[data-popup="exit"]');
      if (!el) return;
      if (sessionSeen('popup:exit')) return;

      var armed = false;
      // Give the visitor a moment before arming, so a stray cursor flick on
      // arrival doesn't fire it.
      setTimeout(function () { armed = true; }, 8000);

      // Seeing the welcome popup earlier must not disqualify the visitor —
      // these are different moments. Only overlap and the cooldown block it.
      function fire() {
        if (!armed || busy()) return;
        markSession('popup:exit');
        open(el);
      }

      if (!isTouch) {
        on(document, 'mouseout', function (e) {
          if (e.relatedTarget || e.toElement) return;   // still inside the page
          if (e.clientY > 6) return;                     // only the top edge counts
          fire();
        });
      } else {
        // Exit intent doesn't exist on touch. Use dwell + scroll depth instead.
        var delay = (Number(el.getAttribute('data-mobile-delay')) || 60) * 1000;
        setTimeout(function () {
          if (window.scrollY > window.innerHeight * 0.5) fire();
        }, delay);
      }

      on(el, 'click', function (e) {
        if (e.target.closest('[data-popup-close]')) close();
      });
    }

    /* --- shared: copy-to-clipboard + a11y --- */
    function initShared() {
      on(document, 'click', function (e) {
        var chip = e.target.closest('[data-copy-code]');
        if (!chip) return;
        var code = chip.getAttribute('data-copy-code');
        var hint = $('[data-copy-hint]', chip);

        function done(msg) {
          if (hint) {
            var prev = hint.textContent;
            hint.textContent = msg;
            setTimeout(function () { hint.textContent = prev; }, 2200);
          }
          Toast('Code ' + code + ' copied.');
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(function () { done('Copied'); })
            .catch(function () { done('Select and copy'); });
        } else {
          done('Select and copy');
        }
      });

      on(document, 'keydown', function (e) {
        if (!openEl) return;
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab') return;
        var f = $$('a[href], button:not([disabled]), input', openEl)
          .filter(function (n) { return n.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    return {
      init: function () { initShared(); initWelcome(); initExit(); },
      validEmail: validEmail
    };
  })();

  /* -------------------------------------------------------------- 17. GO */

  function boot() {
    document.documentElement.classList.add('js');

    var modules = [
      initEmbers, initCursor, initFxDock, initCountdown, initReveals,
      initNavDropdowns, initMobileNavGroups,
      initHeader, initParallax, initHeroWords, initTiles, initMarquee,
      initQty, initAccordion, initGallery, initVariants, initSearch, initQuiz,
      Popups.init
    ];

    modules.forEach(function (fn) {
      try { fn(); } catch (err) {
        if (window.console && console.warn) console.warn('[crypt] module failed:', fn.name, err);
      }
    });

    try { Cart.fetch(false); } catch (e) {}
  }

  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', boot);
  else boot();

  window.CryptTheme = { Toast: Toast, Cart: Cart, Drawers: Drawers, money: money, Popups: Popups };
})();
